<template>
  <div class="customer-create main-content">
    <form action="" @submit.prevent="submitAccountData">
      <div class="page-header">
        <h3 class="page-title">{{ isEdit ? $t('accounts.edit_account') : $t('accounts.new_account') }}</h3>
        <ol class="breadcrumb">
          <li class="breadcrumb-item">
            <router-link slot="item-title" to="/super-admin/dashboard">{{ $t('general.home') }}</router-link>
          </li>
          <li class="breadcrumb-item">
            <router-link slot="item-title" to="/super-admin/accounts">{{ $tc('accounts.account', 2) }}</router-link>
          </li>
          <li class="breadcrumb-item">
            {{ isEdit ? $t('accounts.edit_account') : $t('accounts.new_account') }}
          </li>
        </ol>
        <div class="page-actions header-button-container">
          <base-button
            :loading="isLoading"
            :disabled="isSubmitDisabled"
            :tabindex="23"
            icon="save"
            color="theme"
            type="submit"
          >
            {{ isEdit ? $t('accounts.update_account') : $t('accounts.save_account') }}
          </base-button>
        </div>
      </div>
      <div class="customer-card card">
        <div class="card-body">
          <div class="row">
            <div class="section-title col-sm-2">{{ $t('accounts.basic_info') }}</div>
            <div class="col-sm-5">
              <div class="form-group">
                <label class="form-label">{{ $t('accounts.display_name') }}</label><span class="text-danger"> *</span>
                <base-input
                  :invalid="$v.formData.name.$error"
                  v-model="formData.name"
                  focus
                  type="text"
                  name="name"
                  tab-index="1"
                  @input="$v.formData.name.$touch()"
                />
                <div v-if="$v.formData.name.$error">
                  <span v-if="!$v.formData.name.required" class="text-danger">
                    {{ $tc('validation.name_required') }}
                  </span>
                  <span v-if="!$v.formData.name.minLength" class="text-danger">
                    {{ $tc('validation.name_min_length', $v.formData.name.$params.minLength.min, { count: $v.formData.name.$params.minLength.min }) }}
                  </span>
                </div>
              </div>

              <div class="form-group">
                <label class="form-label">{{ $t('accounts.company_name') }}</label><span class="text-danger"> *</span>
                <base-input
                  :invalid="$v.formData.company_name.$error"
                  v-model="formData.company_name"
                  focus
                  type="text"
                  name="company_name"
                  tab-index="1"
                  @input="$v.formData.company_name.$touch()"
                  />
                <div v-if="$v.formData.company_name.$error">
                  <span v-if="!$v.formData.company_name.required" class="text-danger">
                    {{ $tc('validation.name_required') }}
                  </span>
                  <span v-if="!$v.formData.company_name.minLength" class="text-danger">
                    {{ $tc('validation.name_min_length', $v.formData.company_name.$params.minLength.min, { count: $v.formData.company_name.$params.minLength.min }) }}
                  </span>
                </div>
              </div>

              <div class="form-group">
                <label class="form-label">{{ $t('accounts.email') }}</label><span class="text-danger"> *</span>
                <base-input
                  :invalid="$v.formData.email.$error"
                  v-model.trim="formData.email"
                  type="text"
                  name="email"
                  tab-index="3"
                  @input="$v.formData.email.$touch()"
                />
                <div v-if="$v.formData.email.$error">
                  <span v-if="!$v.formData.email.email" class="text-danger">
                    {{ $tc('validation.email_incorrect') }}
                  </span>
                  <span v-if="!$v.formData.email.required" class="text-danger">
                    {{ $tc('validation.email_required') }}
                  </span>
                </div>
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('accounts.primary_currency') }}</label>
                <base-select
                  v-if="currencies"
                  v-model="currency"
                  :options="currencies"
                  :custom-label="currencyNameWithCode"
                  :allow-empty="false"
                  :searchable="true"
                  :show-labels="false"
                  :tabindex="5"
                  :placeholder="$t('accounts.select_currency')"
                  label="name"
                  track-by="id"
                />
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('accounts.status') }}</label><span class="text-danger"> *</span>
                <base-select
                  v-model="status_change"
                  :options=statusTypes

                  :custom-label="StatusCustomLabel"
                  :allow-empty="false"
                  :show-labels="false"
                  :tabindex="5"
                  :placeholder="$t('accounts.select_status')"
                  label="name"
                  track-by="id"
                />

              </div>

              <div class="form-group">
                <label class="form-label">{{ $t('accounts.account_type') }}</label><span class="text-danger"> *</span>
                <base-select
                  v-model="account_type_change"
                  :options=this.account_types
                  :allow-empty="false"
                  :custom-label="UserTypeCustomLabel"
                  :show-labels="false"
                  :tabindex="5"
                  :placeholder="$t('accounts.select_account_type')"
                  label="name"
                  track-by="id"
                  :selected=this.defaultAccountType
                />

              </div>
            </div>
            <div class="col-sm-5">
              <div class="form-group">
                <label class="form-label">{{ $t('customers.primary_contact_name') }}</label>
                <base-input
                  v-model.trim="formData.contact_name"
                  :label="$t('accounts.contact_name')"
                  type="text"
                  tab-index="2"
                />
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('accounts.phone') }}<span class="text-danger"> *</span></label>
                <base-input
                  :invalid="$v.formData.phone.$error"
                  v-model.trim="formData.phone"
                  type="number"
                  name="phone"
                  tab-index="4"
                />
                <div v-if="$v.formData.phone.$error">
                  <span v-if="!$v.formData.phone.required" class="text-danger">
                    {{ $tc('validation.Number_required') }}
                  </span>
                </div>
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('accounts.website') }}</label>
                <base-input
                  v-model="formData.website"
                  :invalid="$v.formData.website.$error"
                  type="url"
                  tab-index="6"
                  @input="$v.formData.website.$touch()"
                />
                <div v-if="$v.formData.website.$error">
                  <span v-if="!$v.formData.website.url" class="text-danger">
                    {{ $tc('validation.invalid_url') }}
                  </span>
                </div>
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('accounts.role') }}</label><span class="text-danger"> *</span>
                <base-select
                  v-model="role_change"
                  :options=roleTypes
                  :custom-label="RoleCustomLabel"
                  :allow-empty="false"
                  :show-labels="false"
                  :tabindex="5"
                  :placeholder="$t('accounts.select_role')"
                  label="name"
                  track-by="id"
                />

              </div>

               <div class="form-group">
                <label class="form-label">{{ $t('accounts.password') }}</label><span v-if="!isEdit" class="text-danger"> *</span>
                <base-input
                    v-model="formData.password"
                    :invalid="$v.formData.password.$error"
                    type="password"
                    :placeholder="$t('login.enter_password')"
                    show-password
                    @input="$v.formData.password.$touch()"
                />

                <div v-if="$v.formData.password.$error">
                  <span v-if="!$v.formData.password.minLength" class="text-danger"> {{ $tc('validation.password_min_length', $v.formData.password.$params.minLength.min, {count: $v.formData.password.$params.minLength.min}) }} </span>
                  <span v-if="!$v.formData.password.hasCapitalCaseLetter" class="text-danger"> {{ $tc('validation.requireCapitalCaseLetter') }} </span>
                </div>

              </div>

            </div>
          </div>
           <hr> <!-- first row complete  -->
          <div class="row">
            <div class="section-title col-sm-2">{{ $t('accounts.company_address') }}</div>
            <div class="col-sm-5">
              <div class="form-group">
                <label class="form-label">{{ $t('accounts.name') }}</label>
                <base-input
                  v-model.trim="company.name"
                  type="text"
                  name="address_name"
                  tab-index="7"
                />
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('accounts.state') }}</label>
                <base-input
                  v-model="company.state"
                  name="company.state"
                  type="text"
                  tab-index="9"
                />
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('accounts.address') }}</label>
                <base-text-area
                  v-model.trim="company.address_street_1"
                  :tabindex="11"
                  :placeholder="$t('general.street_1')"
                  type="text"
                  rows="2"
                  @input="$v.company.address_street_1.$touch()"
                />
                <div v-if="$v.company.address_street_1.$error">
                  <span v-if="!$v.company.address_street_1.maxLength" class="text-danger">
                    {{ $t('validation.address_maxlength') }}
                  </span>
                </div>
                <base-text-area
                  :tabindex="12"
                  v-model.trim="company.address_street_2"
                  :placeholder="$t('general.street_2')"
                  type="text"
                  rows="2"
                  @input="$v.company.address_street_2.$touch()"
                />
                <div v-if="$v.company.address_street_2.$error">
                  <span v-if="!$v.company.address_street_2.maxLength" class="text-danger">
                    {{ $t('validation.address_maxlength') }}
                  </span>
                </div>
              </div>
            </div>
            <div class="col-sm-5">
              <div class="form-group">
                <label class="form-label">{{ $t('accounts.country') }}</label>
                <base-select
                  v-model="company_country"
                  :options="companyCountries"
                  :searchable="true"
                  :show-labels="false"
                  :allow-empty="true"
                  :tabindex="8"
                  :placeholder="$t('general.select_country')"
                  label="name"
                  track-by="id"
                />
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('accounts.city') }}</label>
                <base-input
                  v-model="company.city"
                  name="company.city"
                  type="text"
                  tab-index="10"
                />
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('accounts.phone') }}</label>
                <base-input
                  v-model.trim="company.phone"
                  type="text"
                  name="phone"
                  tab-index="13"
                />
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('accounts.zip_code') }}</label>
                <base-input
                  v-model.trim="company.zip"
                  type="text"
                  name="zip"
                  tab-index="14"
                />
              </div>
            </div>
          </div>
          <hr> <!-- second row complete  -->
          <div class="same-address-checkbox-container">
            <div class="p-1">
              <base-button ref="sameAddress" icon="copy" color="theme" class="btn-sm" @click="copyCompanyAddress(true)">
                {{ $t('accounts.copy_company_address') }}
              </base-button>
            </div>
          </div>
          <div class="row">
            <div class="section-title col-sm-2">{{ $t('accounts.billing_address') }}</div>
            <div class="col-sm-5">
              <div class="form-group">
                <label class="form-label">{{ $t('accounts.name') }}</label>
                <base-input
                  v-model.trim="billing.name"
                  type="text"
                  name="address_name"
                  tab-index="7"
                />
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('accounts.state') }}</label>
                <base-input
                  v-model="billing.state"
                  name="billing.state"
                  type="text"
                  tab-index="9"
                />
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('accounts.address') }}</label>
                <base-text-area
                  v-model.trim="billing.address_street_1"
                  :tabindex="11"
                  :placeholder="$t('general.street_1')"
                  type="text"
                  name="billing_street1"
                  rows="2"
                  @input="$v.billing.address_street_1.$touch()"
                />
                <div v-if="$v.billing.address_street_1.$error">
                  <span v-if="!$v.billing.address_street_1.maxLength" class="text-danger">
                    {{ $t('validation.address_maxlength') }}
                  </span>
                </div>
                <base-text-area
                  :tabindex="12"
                  v-model.trim="billing.address_street_2"
                  :placeholder="$t('general.street_2')"
                  type="text"
                  name="billing_street2"
                  rows="2"
                  @input="$v.billing.address_street_2.$touch()"
                />
                <div v-if="$v.billing.address_street_2.$error">
                  <span v-if="!$v.billing.address_street_2.maxLength" class="text-danger">
                    {{ $t('validation.address_maxlength') }}
                  </span>
                </div>
              </div>
            </div>
            <div class="col-sm-5">
              <div class="form-group">
                <label class="form-label">{{ $t('accounts.country') }}</label>
                <base-select
                  v-model="billing_country"
                  :options="billingCountries"
                  :searchable="true"
                  :show-labels="false"
                  :allow-empty="true"
                  :tabindex="8"
                  :placeholder="$t('general.select_country')"
                  label="name"
                  track-by="id"
                />
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('accounts.city') }}</label>
                <base-input
                  v-model="billing.city"
                  name="billing.city"
                  type="text"
                  tab-index="10"
                />
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('accounts.phone') }}</label>
                <base-input
                  v-model.trim="billing.phone"
                  type="text"
                  name="phone"
                  tab-index="13"
                />
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('accounts.zip_code') }}</label>
                <base-input
                  v-model.trim="billing.zip"
                  type="text"
                  name="zip"
                  tab-index="14"
                />
              </div>
            </div>
          </div>
          <hr> <!-- third row complete  -->
          <div class="same-address-checkbox-container">
            <div class="p-1">
              <base-button ref="sameAddress" icon="copy" color="theme" class="btn-sm" @click="copyAddress(true)">
                {{ $t('accounts.copy_billing_address') }}
              </base-button>
            </div>
          </div>

          <div class="row">
            <div class="section-title col-sm-2">
              {{ $t('accounts.shipping_address') }}
            </div>
            <div class="col-sm-5">
              <div class="form-group">
                <label class="form-label">{{ $t('accounts.name') }}</label>
                <base-input
                  v-model.trim="shipping.name"
                  type="text"
                  name="address_name"
                  tab-index="15"
                />
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('accounts.state') }}</label>
                <base-input
                  v-model="shipping.state"
                  name="shipping.state"
                  type="text"
                  tab-index="17"
                />
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('accounts.address') }}</label>
                <base-text-area
                  v-model.trim="shipping.address_street_1"
                  :tabindex="19"
                  :placeholder="$t('general.street_1')"
                  type="text"
                  name="street_1"
                  rows="2"
                  @input="$v.shipping.address_street_1.$touch()"
                />
                <div v-if="$v.shipping.address_street_1.$error">
                  <span v-if="!$v.shipping.address_street_1.maxLength" class="text-danger">{{ $t('validation.address_maxlength') }}</span>
                </div>
                <base-text-area
                  v-model.trim="shipping.address_street_2"
                  :tabindex="20"
                  :placeholder="$t('general.street_2')"
                  type="text"
                  name="street_2"
                  rows="2"
                  @input="$v.shipping.address_street_2.$touch()"
                />
                <div v-if="$v.shipping.address_street_2.$error">
                  <span v-if="!$v.shipping.address_street_2.maxLength" class="text-danger">{{ $t('validation.address_maxlength') }}</span>
                </div>
              </div>
            </div>
            <div class="col-sm-5">
              <div class="form-group">
                <label class="form-label">{{ $t('accounts.country') }}</label>
                <base-select
                  v-model="shipping_country"
                  :options="shippingCountries"
                  :searchable="true"
                  :show-labels="false"
                  :tabindex="16"
                  :allow-empty="true"
                  :placeholder="$t('general.select_country')"
                  label="name"
                  track-by="id"
                />
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('accounts.city') }}</label>
                <base-input
                  v-model="shipping.city"
                  name="shipping.city"
                  type="text"
                  tab-index="18"
                />
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('accounts.phone') }}</label>
                <base-input
                  v-model.trim="shipping.phone"
                  type="text"
                  name="phone"
                  tab-index="21"
                />
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('accounts.zip_code') }}</label>
                <base-input
                  v-model.trim="shipping.zip"
                  type="text"
                  name="zip"
                  tab-index="22"
                />
              </div>
              <div class="form-group collapse-button-container">
                <base-button
                  :tabindex="23"
                  icon="save"
                  color="theme"
                  class="collapse-button"
                  type="submit"
                >
                  {{ $t('accounts.save_account') }}
                </base-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'
  import MultiSelect from 'vue-multiselect'
  import { validationMixin } from 'vuelidate'
  import AddressStub from '../../stub/address'
  import {hasCapitalCaseLetter,hasNumber } from './../../validators/password'
  const { helpers, required, minLength, email, url, maxLength, requiredIf } = require('vuelidate/lib/validators')

  export default {
    components: { MultiSelect },
    mixins: [validationMixin],
    data () {
      return {
        isCopyFromBilling: false,
        isCopyFromCompany: false,
        isLoading: false,
        statuses:[],
        roles:[],
        account_types:[],
        defaultAccountType:null,
        status_change: null,
        role_change: null,
        account_type_change: null,
        formData: {
          company_name:null,
          name: null,
          contact_name: null,
          email: null,
          phone: null,
          currency_id: null,
          role: null,
          status: null,
          account_type: null,
          website: null,
          addresses: [],
          password:null,
        },
        currency: null,
        company: {
          name: null,
          country_id: null,
          state: null,
          city: null,
          phone: null,
          zip: null,
          address_street_1: null,
          address_street_2: null,
          type: 'company'
        },
        billing: {
          name: null,
          country_id: null,
          state: null,
          city: null,
          phone: null,
          zip: null,
          address_street_1: null,
          address_street_2: null,
          type: 'billing'
        },
        shipping: {
          name: null,
          country_id: null,
          state: null,
          city: null,
          phone: null,
          zip: null,
          address_street_1: null,
          address_street_2: null,
          type: 'shipping'
        },
        currencyList: [],

        company_country: null,
        billing_country: null,
        shipping_country: null,

        companyCountries: [],
        billingCountries: [],
        shippingCountries: []
      }
    },
    validations: {
      formData: {
        name: {
          required,
          minLength: minLength(3)
        },
        company_name: {
          required,
          minLength: minLength(3)
        },
        email: {
          email,
          required,
        },
        password: {
          required: requiredIf(function () {
            hasNumber(this.formData.password, true),
            hasCapitalCaseLetter(this.formData.password, true)
            return this.isEdit == false 
          }),
          minLength: minLength(8)
        },
        website: {
          url
        },
        phone: {
          required,
        },
      },
      company: {
        address_street_1: {
          maxLength: maxLength(255)
        },
        address_street_2: {
          maxLength: maxLength(255)
        }
      },
      billing: {
        address_street_1: {
          maxLength: maxLength(255)
        },
        address_street_2: {
          maxLength: maxLength(255)
        }
      },
      shipping: {
        address_street_1: {
          maxLength: maxLength(255)
        },
        address_street_2: {
          maxLength: maxLength(255)
        }
      }
    },
    computed: {
      ...mapGetters('user', [
        'defaultAccount',
        'accountTypes',
        'defaultRole',
        'roleTypes',
        'defaultStatus',
        'statusTypes',
        'defaultCountry',
      ]),
      ...mapGetters('currency', [
        'defaultCurrency',
        'currencies',
      ]),
      isEdit () {
        if (this.$route.name === 'accounts.edit') {
          return true
        }
        return false
      },
      hasCompanyAdd () {
        let company = this.company
        if (
          company.name ||
          company.country_id ||
          company.state ||
          company.city ||
          company.phone ||
          company.zip ||
          company.address_street_1 ||
          company.address_street_2) {
          return true
        }
        return false
      },
      hasBillingAdd () {
        let billing = this.billing
        if (
          billing.name ||
          billing.country_id ||
          billing.state ||
          billing.city ||
          billing.phone ||
          billing.zip ||
          billing.address_street_1 ||
          billing.address_street_2) {
          return true
        }
        return false
      },
      hasShippingAdd () {
        let shipping = this.shipping
        if (
          shipping.name ||
          shipping.country_id ||
          shipping.state ||
          shipping.city ||
          shipping.phone ||
          shipping.zip ||
          shipping.address_street_1 ||
          shipping.address_street_2) {
          return true
        }
        return false
      },
           
      isSubmitDisabled() {
      return this.$v.$invalid
    }
    },
    watch: {
      company_country (newCountry) {
        if (newCountry) {
          this.company.country_id = newCountry.id
        } else {
          this.company.country_id = null
        }

      },
      billing_country (newCountry) {
        if (newCountry) {
          this.billing.country_id = newCountry.id
          this.isDisabledBillingState = false
        } else {
          this.billing.country_id = null
        }
      },
      shipping_country (newCountry) {
        if (newCountry) {
          this.shipping.country_id = newCountry.id
          return true
        } else {
          this.shipping.country_id = null
        }
      }
    },
    mounted () {
      this.fetchCountry()
      this.fetchData()
      if (this.isEdit) {
        this.loadAccount()
      } else {
        this.currency = this.defaultCurrency
        this.account_type_change=this.defaultAccount
        this.role_change=this.defaultRole
        this.status_change=this.defaultStatus
        this.company_country=this.defaultCountry
        this.billing_country=this.defaultCountry
        this.shipping_country=this.defaultCountry
      }
    },
    methods: {
      currencyNameWithCode ({name, code}) {
        return `${code} - ${name}`
      },

      RoleCustomLabel ({name})
      {
        return name.toUpperCase()
      },

      StatusCustomLabel ({name})
      {
        return name.toUpperCase()
      },

      UserTypeCustomLabel ({name})
      {

        return name.toUpperCase().replace("_"," ");
      },
      ...mapActions('account', [
        'addAccount',
        'fetchAccounts',
        'fetchAccount',
        'updateAccount'
      ]),
      async fetchData () {
        let response = await this.fetchAccounts()
        this.statuses =  response.data.statuses
        this.roles = response.data.roles
        this.account_types = response.data.account_types
      },
      async loadAccount () {
        let { data: { account, currencies, currency, role , status, account_type, addresses } } = await this.fetchAccount(this.$route.params.id)

        this.formData.id = account.id
        this.formData.name = account.name
        this.formData.contact_name = account.contact_name
        this.formData.email = account.email
        this.formData.phone = account.phone
        this.formData.currency_id = account.currency_id
        this.formData.website = account.website
        this.formData.company_name = account.company_name

        this.role_change = role
        this.status_change = status
        this.account_type_change = account_type
        if (addresses.company_address) {
          this.company = addresses.company_address
          if (addresses.company_address.country_id) {
            this.company_country = this.companyCountries.find((c) => c.id === addresses.company_address.country_id)
          }
        }

        if (addresses.billing_address) {
          this.billing = addresses.billing_address

          if (addresses.billing_address.country_id) {
            this.billing_country = this.billingCountries.find((c) => c.id === addresses.billing_address.country_id)
          }
        }

        if (addresses.shipping_address) {
          this.shipping = addresses.shipping_address

          if (addresses.shipping_address.country_id) {
            this.shipping_country = this.shippingCountries.find((c) => c.id === addresses.shipping_address.country_id)
          }
        }

        this.currencyList = currencies
        this.formData.currency_id = account.currency_id
        this.currency = currency
      },
      async fetchCountry () {
        let res = await window.axios.get('/api/countries')
        if (res) {
          this.companyCountries = res.data.countries
          this.billingCountries = res.data.countries
          this.shippingCountries = res.data.countries
        }
      },
      copyAddress (val) {
        if (val === true) {
          this.isCopyFromBilling = true
          this.shipping = {...this.billing, type: 'shipping'}
          this.shipping_country = this.billing_country
          this.shipping_state = this.billing_state
          this.shipping_city = this.billing_city
        } else {
          this.shipping = {...AddressStub, type: 'shipping'}
          this.shipping_country = null
          this.shipping_state = null
          this.shipping_city = null
        }
      },
      copyCompanyAddress (val) {
        if (val === true) {
          this.isCopyFromCompany = true
          this.billing = {...this.company, type: 'billing'}
          this.billing_country = this.company_country
          this.billing_state = this.company_state
          this.billing_city = this.company_city
        } else {
          this.billing = {...AddressStub, type: 'billing'}
          this.billing_country = null
          this.billing_state = null
          this.billing_city = null
        }
      },
      async submitAccountData () {
        this.$v.formData.$touch()
        if (this.$v.$invalid) {
          return true
        }

        if (this.hasBillingAdd && this.hasShippingAdd && this.hasCompanyAdd) {
          this.formData.addresses = [{...this.company},{...this.billing}, {...this.shipping}]
        } else {
          if (this.hasCompanyAdd) {
            this.formData.addresses = [{...this.company}]
          }
          if (this.hasBillingAdd) {
            this.formData.addresses = [{...this.billing}]
          }
          if (this.hasShippingAdd) {
            this.formData.addresses = [{...this.shipping}]
          }
        }

        if (this.isEdit) {
          if (this.currency) {
            this.formData.currency_id = this.currency.id
          }
          if(this.account_type_change){
            this.formData.account_type=this.account_type_change.name
          }
          if(this.role_change){
            this.formData.role=this.role_change.name
          }
          if(this.status_change){
            this.formData.status=this.status_change.name
          }
          this.isLoading = true
          try {
            let response = await this.updateAccount(this.formData)
            if (response.data.success) {
              window.toastr['success'](this.$t('accounts.account_updated_message'))
              this.$router.push('/super-admin/accounts')
              this.isLoading = false
              return true
            } else {
              this.isLoading = false
              if (response.data.emailDuplicate) {
                window.toastr['error'](this.$t('validation.email_already_taken'))
              }
              if (response.data.companyDuplicate) {
                window.toastr['error'](this.$t('validation.company_already_taken'))
              }
            }
          } catch (err) {
            this.isLoading = false
            if (err.response && err.response.data && err.response.data.errors && err.response.data.errors.email) {
              window.toastr['error'](this.$t('validation.email_already_taken'))
            }

          }
        } else {
          this.isLoading = true
          if (this.currency) {
            this.isLoading = true
            this.formData.currency_id = this.currency.id
            }
          if(this.account_type_change){
            this.isLoading = true
            this.formData.account_type=this.account_type_change.name
          }
          if(this.role_change){
            this.isLoading = true
            this.formData.role=this.role_change.name
          }
          if(this.status_change){
            this.isLoading = true
            this.formData.status=this.status_change.name
          }
          try {
            let response = await this.addAccount(this.formData)

            if (response.data.success) {
              window.toastr['success'](this.$t('accounts.account_created_message'))
              this.$router.push('/super-admin/accounts')
              this.isLoading = false
              return true
            }
          } catch (err) {
            if (err.response.data.email) {
              this.isLoading = false
              window.toastr['error'](this.$t('validation.email_already_taken'))
            }
            if (err.response.data.company_name) {
              this.isLoading = false
              window.toastr['error'](this.$t('validation.company_already_taken'))
            }
        }
        }
      }
    }
  }
</script>
